import { debounce } from 'lodash-es';
import { ICompanyFinancialsDataModel } from '../../data-models/company-financials.data-model';
import {
  IKPIRequestBulkSendResponse,
  IKPIRequestDataModel,
  IKPIRequestResponse,
  IKPIResponseActionPayload,
  KPIRequestResponse,
  KPIRequestStatus,
} from '../../data-models/kpi-requests.data-model';
import { EmailTemplateMessage } from '../../data-models/preferences-v3.data-model';
import { IBulkUpdateKpiCustomDataPayload } from '../../pages/CompanyProfiles/components/Financials/hooks/useCustomKPIActions';
import { CreateKpiRequestPayload } from '../../pages/KPI/components/KPICompanies/AllCompaniesForms/useCreateKpiRequest';
import { IFinancialsRowData } from '../../pages/KPI/components/KPIRequests/KPIRequestResponse/MSFinancialsGrid';
import { MaggieHttp } from '../MaggieHttp';

export async function sendKPIRequests(requestsId: number[]) {
  return await MaggieHttp.maggie.put<{ requestIds: number[] }, IKPIRequestBulkSendResponse>(
    `/kpiRequest/bulkSend`,
    {
      requestIds: requestsId,
    }
  );
}

export async function createKpiRequest(request: CreateKpiRequestPayload) {
  const { data } = await MaggieHttp.maggie.post<Partial<IKPIRequestDataModel>, IKPIRequestDataModel>(
    `/kpiRequest`,
    request
  );
  return data;
}

export async function deleteKPIRequest(requestId: number) {
  const { data } = await MaggieHttp.maggie.delete<Partial<IKPIRequestDataModel>>(`/kpiRequest/${requestId}`);

  return data;
}

interface IFetchKpiRequestsParams {
  status?: KPIRequestStatus;
  companyId?: number;
}
export async function fetchKPIRequests({ status, companyId }: IFetchKpiRequestsParams = {}): Promise<
  IKPIRequestDataModel[]
> {
  const { data } = await MaggieHttp.maggie.get<IKPIRequestDataModel[]>('/kpiRequest', {
    params: {
      status,
      companyId,
    },
  });

  return data;
}

export async function fetchKPIRequestById(requestId: number): Promise<KPIRequestResponse> {
  const { data } = await MaggieHttp.maggie.get<KPIRequestResponse>(`/kpiRequest/${requestId}`);

  return data;
}

export async function createKPIRequestResponse(
  requestId: string,
  responseData: Partial<IKPIResponseActionPayload>,
  autosave?: boolean
) {
  const { data } = await MaggieHttp.maggie.post<Partial<IKPIResponseActionPayload>, IKPIRequestResponse>(
    `/kpiRequest/${requestId}/response`,
    responseData,
    {
      params: {
        autosave,
      },
    }
  );

  return data;
}

export async function acceptKPIRequestResponse(requestId: string, acceptMessage?: string) {
  const { data } = await MaggieHttp.maggie.put<Partial<IKPIRequestDataModel>, IKPIRequestDataModel>(
    `/kpiRequest/${requestId}/accept`,
    {
      acceptMessage,
    }
  );
  return data;
}

interface IRejectPayload {
  observation?: string;
  rejectReason?: string;
}
export async function rejectKPIRequestResponse(requestId: string, comment?: string) {
  const { data } = await MaggieHttp.maggie.put<IRejectPayload, IKPIRequestDataModel>(
    `/kpiRequest/${requestId}/reject`,
    {
      observation: comment,
      rejectReason: comment,
    }
  );

  return data;
}

export async function updateKPIRequestResponse(
  requestId: string,
  responseFormData: Partial<IKPIResponseActionPayload>,
  autosave?: boolean
) {
  const { data } = await MaggieHttp.maggie.put<Partial<IKPIResponseActionPayload>, IKPIRequestResponse>(
    `/kpiRequest/${requestId}/response`,
    { ...responseFormData },
    { params: { autosave } }
  );

  return data;
}

export async function saveResponseDraft(
  requestId: string,
  responseFormData: Partial<IKPIResponseActionPayload>
) {
  const { data } = await MaggieHttp.maggie.put<Partial<IKPIResponseActionPayload>, IKPIRequestResponse>(
    `/kpiRequest/${requestId}/response`,
    { ...responseFormData },
    { params: { autosave: true } }
  );

  return data;
}

export const saveResponseDraftDebounced = debounce(saveResponseDraft, 1000);

export async function fetchKPIRequestResponseById(requestId: number): Promise<IKPIRequestResponse> {
  const { data } = await MaggieHttp.maggie.get<IKPIRequestResponse>(`/kpiRequest/${requestId}/response`);

  return data;
}

export async function fetchAssignedKPIRequests() {
  const { data } = await MaggieHttp.maggie.get<IKPIRequestDataModel[]>(`/kpiRequest/assigned`);

  return data;
}

interface IFileS3URL {
  companyId: number;
  fileId: string;
  contentType: string;
  fileName?: string;
}
interface IFileUploadResponse {
  companyId: number;
  fileId: string;
  signedUrl: string;
}

export async function fetchKPIRequestS3FileUploadUrl({
  contentType,
  companyId,
}: Partial<IFileS3URL>): Promise<IFileUploadResponse> {
  const { data } = await MaggieHttp.maggie.post<Partial<IFileS3URL>, IFileUploadResponse>(`/file-upload`, {
    companyId,
    contentType,
  });

  return data;
}

export async function fetchKPIRequestUploadedFileFromS3({
  companyId,
  fileId,
  contentType,
  fileName,
}: IFileS3URL): Promise<string> {
  const { data } = await MaggieHttp.maggie.post<IFileS3URL, string>('file-upload/retrieve', {
    companyId,
    contentType,
    fileId,
    fileName,
  });

  return data;
}

interface IBulkKpiRequestUpdate {
  action: 'accept' | 'reject';
  requestIds: number[];
}

export async function bulkAcceptResponses(ids: number[]) {
  await MaggieHttp.maggie.put<IBulkKpiRequestUpdate, unknown>(`/kpiRequest/bulkUpload`, {
    action: 'accept',
    requestIds: ids,
  });
}

/** `pin`: pin the written value as the cell's value (FD-5414); omitted, the org's `pinUserEdits` setting decides. */
export type ICompanyFinancialsDataModelCreatePayload = Omit<
  ICompanyFinancialsDataModel,
  'id' | 'iLevelId' | 'updatedAt' | 'createdAt'
> & { pin?: boolean };
export type ICompanyFinancialsDataModelUpdatePayload = Partial<ICompanyFinancialsDataModel> & {
  pin?: boolean;
};
export async function createCustomKPIData(kpiData: ICompanyFinancialsDataModelCreatePayload) {
  const { data } = await MaggieHttp.maggie.post<
    ICompanyFinancialsDataModelCreatePayload,
    ICompanyFinancialsDataModel
  >(`/kpiCustomData`, kpiData);
  return data;
}
export async function updateCustomKPIData(id: number, kpiData: ICompanyFinancialsDataModelUpdatePayload) {
  const { data } = await MaggieHttp.maggie.put<
    ICompanyFinancialsDataModelUpdatePayload,
    ICompanyFinancialsDataModel
  >(`/kpiCustomData/${id}`, kpiData);
  return data;
}

export async function deleteCustomKPIData(id: number) {
  await MaggieHttp.maggie.delete(`/kpiCustomData/${id}`);
}

export async function bulkUpdateCustomKpiData(payload: IBulkUpdateKpiCustomDataPayload) {
  await MaggieHttp.maggie.post<IBulkUpdateKpiCustomDataPayload, unknown>(`/kpiCustomData/bulk`, payload);
}

export async function moveRequestBackToReview(requestId: number) {
  const { data } = await MaggieHttp.maggie.put<number, IKPIRequestDataModel>(
    `/kpiRequest/backToReview/${requestId}`
  );
  return data;
}

export async function managedServiceStartReview(requestId: number) {
  const { data } = await MaggieHttp.maggie.put<number, IKPIRequestDataModel>(
    `/kpiRequest/managed-service/in-progress/${requestId}`
  );
  return data;
}

export async function managedServiceFinishReview(requestId: number) {
  const { data } = await MaggieHttp.maggie.put<number, IKPIRequestDataModel>(
    `/kpiRequest/managed-service/finished/${requestId}`
  );
  return data;
}

interface IMsEnteredRequestData {
  financialsData: IFinancialsRowData[];
}
export async function saveRequestData(requestId: number, financialsData: IFinancialsRowData[]) {
  const { data } = await MaggieHttp.maggie.put<{ data: IMsEnteredRequestData | null }, IMsEnteredRequestData>(
    `/kpiRequest/${requestId}/data`,
    { data: { financialsData } }
  );
  return data;
}

export async function getRequestData(requestId: number) {
  const { data } = await MaggieHttp.maggie.get<{ data: IMsEnteredRequestData | null }>(
    `/kpiRequest/${requestId}/data`
  );
  return data;
}

export type DataCollectionTemplateDefaultMessages = Record<
  'request' | 'reminder' | 'rejected',
  EmailTemplateMessage & { cta?: string }
>;

export async function fetchDataCollectionTemplateDefaults() {
  const { data } = await MaggieHttp.maggie.get<DataCollectionTemplateDefaultMessages>(
    '/kpiRequest/data-collection/template-defaults'
  );
  return data;
}
